import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import {
  TrendingUp,
  Eye,
  MousePointerClick,
  BarChart3,
  Sparkles,
  AlertCircle,
  ArrowUpRight
} from 'lucide-react'

export function Dashboard() {
  const { data: features, isLoading: featuresLoading } = useQuery({
    queryKey: ['features'],
    queryFn: () => api.getFeatures(),
  })

  const { data: seo, isLoading: seoLoading } = useQuery({
    queryKey: ['seo', 'overview'],
    queryFn: () => api.getSEOOverview(),
    enabled: !!features?.features.seo.googleSearchConsole,
  })

  const { data: pulse, isLoading: pulseLoading } = useQuery({
    queryKey: ['intelligence', 'pulse'],
    queryFn: () => api.getIntelligencePulse(),
  })

  const { data: staging } = useQuery({
    queryKey: ['ai-content', 'staging'],
    queryFn: () => api.getStagingQueue(),
    enabled: features?.features.aiContentGeneration,
  })

  const isLoading = featuresLoading || seoLoading || pulseLoading

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card h-32 bg-slate-100" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card h-64 bg-slate-100" />
          <div className="card h-64 bg-slate-100" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Clicks"
          value={seo?.totalClicks?.toLocaleString() || '0'}
          icon={MousePointerClick}
          color="blue"
        />
        <StatCard
          title="Impressions"
          value={seo?.totalImpressions?.toLocaleString() || '0'}
          icon={Eye}
          color="green"
        />
        <StatCard
          title="Average CTR"
          value={`${((seo?.averageCTR || 0) * 100).toFixed(1)}%`}
          icon={TrendingUp}
          color="purple"
        />
        <StatCard
          title="Avg Position"
          value={seo?.averagePosition?.toFixed(1) || '0'}
          icon={BarChart3}
          color="orange"
        />
      </div>

      {/* Features Status */}
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Platform Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <FeatureBadge
            name="Twitter/X"
            enabled={features?.features.platforms.twitter}
          />
          <FeatureBadge
            name="LinkedIn"
            enabled={features?.features.platforms.linkedin}
          />
          <FeatureBadge
            name="Reddit"
            enabled={features?.features.platforms.reddit}
          />
          <FeatureBadge
            name="Claude AI"
            enabled={features?.features.ai.anthropic}
          />
          <FeatureBadge
            name="GPT"
            enabled={features?.features.ai.openai}
          />
          <FeatureBadge
            name="Gemini"
            enabled={features?.features.ai.gemini}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Moderation Queue */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">AI Content Queue</h2>
            {staging && staging.length > 0 && (
              <span className="badge-warning">{staging.length} pending</span>
            )}
          </div>

          {staging && staging.length > 0 ? (
            <div className="space-y-3">
              {staging.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Sparkles className="w-5 h-5 text-primary-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {item.generatedContent.title || item.originalRequest.platform}
                    </p>
                    <p className="text-xs text-slate-500">
                      {item.aiProvider} • Score: {item.metadata.estimatedViralScore}/100
                    </p>
                  </div>
                  <span className="badge-info text-xs">
                    {item.originalRequest.platform}
                  </span>
                </div>
              ))}
              {staging.length > 3 && (
                <p className="text-sm text-slate-500 text-center">
                  +{staging.length - 3} more in queue
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No content awaiting moderation</p>
            </div>
          )}
        </div>

        {/* Trends & Opportunities */}
        <div className="card">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Opportunities</h2>

          {pulse?.opportunities && pulse.opportunities.length > 0 ? (
            <div className="space-y-3">
              {pulse.opportunities.slice(0, 4).map((opp) => (
                <div key={opp.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`p-1.5 rounded ${
                    opp.priority === 'urgent' ? 'bg-red-100' :
                    opp.priority === 'high' ? 'bg-orange-100' :
                    opp.priority === 'medium' ? 'bg-yellow-100' : 'bg-slate-100'
                  }`}>
                    <AlertCircle className={`w-4 h-4 ${
                      opp.priority === 'urgent' ? 'text-red-600' :
                      opp.priority === 'high' ? 'text-orange-600' :
                      opp.priority === 'medium' ? 'text-yellow-600' : 'text-slate-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{opp.title}</p>
                    <p className="text-xs text-slate-500 truncate">{opp.description}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No opportunities detected yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">System Info</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Version</p>
            <p className="font-medium text-slate-900">{features?.version || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-slate-500">Signal</p>
            <p className="font-medium text-slate-900">
              {features?.features.signalIntegration ? 'Connected' : 'Disabled'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">SEO Sources</p>
            <p className="font-medium text-slate-900">
              {[
                features?.features.seo.googleSearchConsole && 'GSC',
                features?.features.seo.googleAnalytics && 'GA4',
                features?.features.seo.bingWebmaster && 'Bing',
              ].filter(Boolean).join(', ') || 'None'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">AI Providers</p>
            <p className="font-medium text-slate-900">
              {features?.features.ai.available
                ? [
                    features.features.ai.anthropic && 'Claude',
                    features.features.ai.openai && 'GPT',
                    features.features.ai.gemini && 'Gemini',
                  ].filter(Boolean).join(', ')
                : 'None configured'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string
  value: string
  icon: typeof TrendingUp
  color: 'blue' | 'green' | 'purple' | 'orange'
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  }

  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  )
}

function FeatureBadge({ name, enabled }: { name: string; enabled?: boolean }) {
  return (
    <div className={`px-3 py-2 rounded-lg text-center text-sm ${
      enabled
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-slate-100 text-slate-400 border border-slate-200'
    }`}>
      {name}
    </div>
  )
}
